import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  increment,
  serverTimestamp,
  QueryConstraint,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { SharedContent, UserProfile, Portfolio } from '../types';
import { UserService } from './userService';
import { PortfolioService } from './portfolioService';

export class SharedContentService {
  private static readonly COLLECTION_NAME = 'sharedContent';

  /**
   * Share a watchlist
   */
  static async shareWatchlist(userId: string, profile: UserProfile): Promise<void> {
    console.log('[shareWatchlist] Input profile:', profile);
    
    const sharedDoc: any = {
      userId,
      contentType: 'watchlist',
      sharedAt: new Date(),
      lastUpdated: new Date(),
      isActive: true,
      ownerDisplayName: profile.displayName || profile.email,
      viewCount: 0,
      copyCount: 0,
    };

    // Only add optional fields if they have values
    if (profile.handle) {
      console.log('[shareWatchlist] Adding handle:', profile.handle);
      sharedDoc.ownerHandle = profile.handle;
    } else {
      console.log('[shareWatchlist] No handle found');
    }
    
    if (profile.profilePicUrl) {
      console.log('[shareWatchlist] Adding profile pic:', profile.profilePicUrl);
      sharedDoc.ownerProfilePic = profile.profilePicUrl;
    } else {
      console.log('[shareWatchlist] No profile pic found');
    }

    console.log('[shareWatchlist] Final sharedDoc before Firestore:', sharedDoc);

    // Use batch write to ensure atomicity
    const batch = writeBatch(db);

    // 1. Create the shared content record
    batch.set(
      doc(db, this.COLLECTION_NAME, `watchlist_${userId}`),
      {
        ...sharedDoc,
        sharedAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
      }
    );

    // 2. Update all watchlist entries for this user to mark them as shared
    const watchlistQuery = query(
      collection(db, 'watchlist'),
      where('userId', '==', userId),
      where('isTracking', '!=', false) // Only update active entries
    );
    
    const watchlistSnapshot = await getDocs(watchlistQuery);
    watchlistSnapshot.forEach((doc) => {
      batch.update(doc.ref, {
        isShared: true,
        lastUpdated: serverTimestamp(),
      });
    });

    await batch.commit();
  }

  /**
   * Unshare a watchlist
   */
  static async unshareWatchlist(userId: string): Promise<void> {
    // Use batch write to ensure atomicity
    const batch = writeBatch(db);

    // 1. Delete the shared content record
    batch.delete(doc(db, this.COLLECTION_NAME, `watchlist_${userId}`));

    // 2. Update all watchlist entries for this user to mark them as not shared
    const watchlistQuery = query(
      collection(db, 'watchlist'),
      where('userId', '==', userId)
    );
    
    const watchlistSnapshot = await getDocs(watchlistQuery);
    watchlistSnapshot.forEach((doc) => {
      batch.update(doc.ref, {
        isShared: false,
        lastUpdated: serverTimestamp(),
      });
    });

    await batch.commit();
  }

  /**
   * Share a portfolio
   */
  static async sharePortfolio(
    portfolio: Portfolio,
    profile: UserProfile,
    sharedSettings?: Portfolio['sharedSettings']
  ): Promise<void> {
    console.log('[sharePortfolio] Input portfolio:', portfolio.id);
    console.log('[sharePortfolio] Input profile:', profile);
    console.log('[sharePortfolio] Shared settings:', sharedSettings);
    
    const sharedDoc: any = {
      userId: portfolio.userId,
      contentType: 'portfolio',
      portfolioId: portfolio.id,
      sharedAt: new Date(),
      lastUpdated: new Date(),
      isActive: true,
      ownerDisplayName: profile.displayName || profile.email,
      viewCount: 0,
      copyCount: 0,
    };

    // Only add optional fields if they have values
    if (profile.handle) {
      sharedDoc.ownerHandle = profile.handle;
    }
    if (profile.profilePicUrl) {
      sharedDoc.ownerProfilePic = profile.profilePicUrl;
    }

    // Use batch write to ensure atomicity
    const batch = writeBatch(db);

    // 1. Create the shared content record
    batch.set(
      doc(db, this.COLLECTION_NAME, `portfolio_${portfolio.id}`),
      {
        ...sharedDoc,
        sharedAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
      }
    );

    // 2. Update the portfolio to mark it as shared
    batch.update(doc(db, 'portfolios', portfolio.id), {
      isShared: true,
      sharedAt: serverTimestamp(),
      sharedSettings: sharedSettings || {
        showHoldings: true,
        showPerformance: true,
        showRules: false
      },
      updatedAt: serverTimestamp(),
    });

    await batch.commit();
    console.log('[sharePortfolio] Portfolio shared successfully');
  }

  /**
   * Unshare a portfolio
   */
  static async unsharePortfolio(portfolioId: string): Promise<void> {
    console.log('[unsharePortfolio] Unsharing portfolio:', portfolioId);
    
    // Use batch write to ensure atomicity
    const batch = writeBatch(db);

    // 1. Delete the shared content record
    batch.delete(doc(db, this.COLLECTION_NAME, `portfolio_${portfolioId}`));

    // 2. Update the portfolio to mark it as not shared
    batch.update(doc(db, 'portfolios', portfolioId), {
      isShared: false,
      sharedAt: null,
      sharedSettings: null,
      updatedAt: serverTimestamp(),
    });

    await batch.commit();
    console.log('[unsharePortfolio] Portfolio unshared successfully');
  }

  /**
   * Update shared content metadata (e.g., when profile changes)
   */
  static async updateSharedContentMetadata(
    userId: string,
    profile: UserProfile
  ): Promise<void> {
    // Update watchlist share if exists
    const watchlistRef = doc(db, this.COLLECTION_NAME, `watchlist_${userId}`);
    const watchlistDoc = await getDoc(watchlistRef);
    if (watchlistDoc.exists()) {
      const updateData: any = {
        ownerDisplayName: profile.displayName || profile.email,
        lastUpdated: serverTimestamp(),
      };

      // Only add optional fields if they have values
      if (profile.handle) {
        updateData.ownerHandle = profile.handle;
      }
      if (profile.profilePicUrl) {
        updateData.ownerProfilePic = profile.profilePicUrl;
      }

      await updateDoc(watchlistRef, updateData);
    }

    // Update portfolio shares
    const portfoliosQuery = query(
      collection(db, this.COLLECTION_NAME),
      where('userId', '==', userId),
      where('contentType', '==', 'portfolio')
    );
    const portfolioDocs = await getDocs(portfoliosQuery);
    
    for (const portfolioDoc of portfolioDocs.docs) {
      const updateData: any = {
        ownerDisplayName: profile.displayName || profile.email,
        lastUpdated: serverTimestamp(),
      };

      // Only add optional fields if they have values
      if (profile.handle) {
        updateData.ownerHandle = profile.handle;
      }
      if (profile.profilePicUrl) {
        updateData.ownerProfilePic = profile.profilePicUrl;
      }

      await updateDoc(portfolioDoc.ref, updateData);
    }
  }

  /**
   * Get shared content for discovery
   */
  static async getSharedContent(
    contentType?: 'watchlist' | 'portfolio',
    sortBy: 'recent' | 'popular' = 'recent',
    limitCount: number = 20
  ): Promise<SharedContent[]> {
    const constraints: QueryConstraint[] = [
      where('isActive', '==', true),
    ];

    if (contentType) {
      constraints.push(where('contentType', '==', contentType));
    }

    if (sortBy === 'popular') {
      constraints.push(orderBy('viewCount', 'desc'));
    } else {
      constraints.push(orderBy('sharedAt', 'desc'));
    }

    constraints.push(limit(limitCount));

    const q = query(collection(db, this.COLLECTION_NAME), ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      sharedAt: doc.data().sharedAt?.toDate() || new Date(),
      lastUpdated: doc.data().lastUpdated?.toDate() || new Date(),
    } as SharedContent));
  }

  /**
   * Get shared content by user
   */
  static async getUserSharedContent(userId: string): Promise<SharedContent[]> {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('userId', '==', userId),
      where('isActive', '==', true),
      orderBy('sharedAt', 'desc')
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      sharedAt: doc.data().sharedAt?.toDate() || new Date(),
      lastUpdated: doc.data().lastUpdated?.toDate() || new Date(),
    } as SharedContent));
  }

  /**
   * Get a specific shared content item
   */
  static async getSharedContentById(id: string): Promise<SharedContent | null> {
    const docRef = doc(db, this.COLLECTION_NAME, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      sharedAt: data.sharedAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    } as SharedContent;
  }

  /**
   * Check if content is shared
   */
  static async isContentShared(
    contentId: string,
    contentType: 'watchlist' | 'portfolio'
  ): Promise<boolean> {
    try {
      const docId = contentType === 'watchlist'
        ? `watchlist_${contentId}`
        : `portfolio_${contentId}`;
      
      const docRef = doc(db, this.COLLECTION_NAME, docId);
      const docSnap = await getDoc(docRef);

      return docSnap.exists() && docSnap.data()?.isActive === true;
    } catch (error) {
      console.error('Error checking if content is shared:', error);
      // If we can't read it, assume it's not shared
      return false;
    }
  }

  /**
   * Increment view count
   */
  static async incrementViewCount(id: string): Promise<void> {
    const docRef = doc(db, this.COLLECTION_NAME, id);
    await updateDoc(docRef, {
      viewCount: increment(1),
    });
  }

  /**
   * Increment copy count
   */
  static async incrementCopyCount(id: string): Promise<void> {
    const docRef = doc(db, this.COLLECTION_NAME, id);
    await updateDoc(docRef, {
      copyCount: increment(1),
    });
  }

  /**
   * Search shared content by handle
   */
  static async searchByHandle(handle: string): Promise<SharedContent[]> {
    if (!handle) return [];

    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('ownerHandle', '==', handle),
      where('isActive', '==', true),
      orderBy('sharedAt', 'desc')
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      sharedAt: doc.data().sharedAt?.toDate() || new Date(),
      lastUpdated: doc.data().lastUpdated?.toDate() || new Date(),
    } as SharedContent));
  }
}